// ── SalesDashboardPage — pipeline summary (Sales app landing) ──
// The Pipeline board (funnel + kanban) lives here too, under Pipeline by
// Stage; the old Pipeline page route renders this page. Clicking a stage row
// filters the board to that stage and scrolls to it.
import { useState, useEffect, useRef } from "react";
import { PipelinePage } from "./PipelinePage";
import { B } from "../../../shared/theme";
import { fmt } from "../../../shared/format";
import { Card, ErrorMsg, SectionHeader, Spinner } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

export const SalesDashboardPage = () => {
  const { api, currentUser, go } = useApp();
  const [stages, setStages] = useState([]);
  const [opps, setOpps] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [boardStage, setBoardStage] = useState("");
  const boardRef = useRef(null);

  // silent = refresh the numbers without the spinner (after a drag on the
  // board), so the board below is not torn down and rebuilt.
  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([api.get("getOpportunityStages"), api.get("getOpportunities", { open: 1 }), api.get("getLeads", { open: 1 })])
      .then(([s, o, l]) => { setStages(Array.isArray(s) ? s : []); setOpps(Array.isArray(o) ? o : []); setLeads(Array.isArray(l) ? l : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={() => load()} />;

  const weighted = (x) => parseFloat(x.commitment_amount || 0) * (parseFloat(x.probability != null ? x.probability : x.stage_probability || 0) / 100);
  const openStages = stages.filter(s => !s.is_closed);
  const holdOpps = opps.filter(o => (o.stage || "") === "Hold");
  const activeOpps = opps.filter(o => (o.stage || "") !== "Hold");
  const pipelineTotal = opps.reduce((s, x) => s + parseFloat(x.commitment_amount || 0), 0);
  const weightedTotal = opps.reduce((s, x) => s + weighted(x), 0);

  const cardTitle = { fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight };
  const stat = { fontSize:26, fontWeight:600, lineHeight:1.1 };
  const statSub = { fontSize:11, color:"var(--color-text-secondary)", marginTop:4 };
  const fundHead    = { fontSize:12, fontWeight:700, color:"var(--color-text-secondary)", padding:"3px 0", textAlign:"left" };
  const fundHeadNum = { ...fundHead, textAlign:"right" };
  const fundCell    = { fontSize:12, padding:"4px 0", textAlign:"left" };
  const fundCellNum = { ...fundCell, textAlign:"right", fontVariantNumeric:"tabular-nums" };

  return <>
    <SectionHeader title={"Sales — " + (currentUser?.name || "")} />

    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:16, marginBottom:16 }}>
      <Card style={{ padding:16, cursor:"pointer" }} onClick={() => go("sales-leads", { open: 1 })}>
        <div style={cardTitle}>Open Leads</div>
        <div style={stat}>{leads.length}</div>
        <div style={statSub}>Not yet converted</div>
      </Card>
      <Card style={{ padding:16, cursor:"pointer" }} onClick={() => go("sales-opportunities", { open: 1 })}>
        <div style={cardTitle}>Open Opportunities</div>
        <div style={stat}>{activeOpps.length}{holdOpps.length > 0 && <span style={{ fontSize:13, fontWeight:400, color:"var(--color-text-secondary)", marginLeft:6 }}>({holdOpps.length} on hold)</span>}</div>
        <div style={statSub}>In the pipeline{holdOpps.length > 0 ? ", excluding hold" : ""}</div>
      </Card>
      <Card style={{ padding:16 }}>
        <div style={cardTitle}>Pipeline Value</div>
        <div style={stat}>{fmt(pipelineTotal)}</div>
        <div style={statSub}>Sum of open opportunity amounts</div>
      </Card>
      <Card style={{ padding:16 }}>
        <div style={cardTitle}>Weighted Forecast</div>
        <div style={stat}>{fmt(weightedTotal)}</div>
        <div style={statSub}>Amount × probability</div>
      </Card>
    </div>

    <Card style={{ padding:16 }}>
      <div style={cardTitle}>Pipeline by Stage</div>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead><tr>
          <td style={fundHead}>Stage</td>
          <td style={fundHeadNum}>Count</td>
          <td style={fundHeadNum}>Amount</td>
          <td style={fundHeadNum}>Weighted</td>
        </tr></thead>
        <tbody>
          {openStages.map(s => {
            const list = opps.filter(o => o.stage === s.stage);
            return (
              <tr key={s.stage} title={"Show " + s.stage + " on the board below"}
                style={{ cursor:"pointer", background: boardStage === s.stage ? B.cyanLight : "" }}
                onClick={() => {
                  setBoardStage(boardStage === s.stage ? "" : s.stage);
                  if (boardRef.current) boardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = boardStage === s.stage ? B.cyanLight : ""}>
                <td style={fundCell}>{s.stage}</td>
                <td style={fundCellNum}>{list.length}</td>
                <td style={fundCellNum}>{fmt(list.reduce((a, x) => a + parseFloat(x.commitment_amount || 0), 0))}</td>
                <td style={fundCellNum}>{fmt(list.reduce((a, x) => a + weighted(x), 0))}</td>
              </tr>
            );
          })}
          <tr style={{ borderTop:"1.5px solid var(--color-border-secondary)" }}>
            <td style={{ ...fundCell, fontWeight:700 }}>Total</td>
            <td style={{ ...fundCellNum, fontWeight:700 }}>{opps.length}</td>
            <td style={{ ...fundCellNum, fontWeight:700 }}>{fmt(pipelineTotal)}</td>
            <td style={{ ...fundCellNum, fontWeight:700 }}>{fmt(weightedTotal)}</td>
          </tr>
        </tbody>
      </table>
    </Card>

    <div ref={boardRef}>
      <PipelinePage embedded stage={boardStage} onStage={setBoardStage} onChange={() => load(true)} />
    </div>
  </>;
};
